// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
// Right-side drawer that shows the platform-wide Action History. Mirrors the
// existing data-room Activity panel (Chakra Drawer, right placement). It loads
// recent admin actions via useActionHistory, then applies the Action / User /
// date-range filters client-side over the loaded records, shows a record count,
// and renders the sortable results table with empty/error/loading states.
import { FC, useEffect, useMemo, useState } from 'react';
import {
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { EmptyState } from '../../ui/errors/empty-state';
import { ErrorState } from '../../ui/errors/error-state';
import { pluralize } from '../../utils/pluralize/pluralize';
import {
  ActionHistoryFilters,
  ActionHistoryFilterState,
  emptyActionHistoryFilterState,
} from './action-history-filters';
import { ActionHistoryTable } from './action-history-table';
import { useActionHistory } from './use-action-history';

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

// Start of the day AFTER the "to" date, so the To filter is inclusive.
const endOfDayExclusive = (date: Date): number => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + 1);
  return next.getTime();
};

const startOfDay = (date: Date): number => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
};

export const ActionHistoryPanel: FC<Props> = ({ isOpen, onClose }) => {
  const { entries, loading, error, retry } = useActionHistory(isOpen);
  const [filter, setFilter] = useState<ActionHistoryFilterState>(
    emptyActionHistoryFilterState
  );

  // Reset filters each time the drawer is opened.
  useEffect(() => {
    if (isOpen) {
      setFilter(emptyActionHistoryFilterState);
    }
  }, [isOpen]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (filter.actionType && entry.actionType !== filter.actionType) {
        return false;
      }
      if (filter.userId && entry.actor.id !== filter.userId) {
        return false;
      }
      const entryTime = new Date(entry.timestamp).getTime();
      if (filter.from && entryTime < startOfDay(filter.from)) {
        return false;
      }
      if (filter.to && entryTime >= endOfDayExclusive(filter.to)) {
        return false;
      }
      return true;
    });
  }, [entries, filter]);

  return (
    <Drawer size="xl" placement="right" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton zIndex={1} data-testid="close-action-history-btn" />
        <DrawerHeader>
          <Heading size="lg">Action History</Heading>
          <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
            All user management operations across the platform
          </Text>
        </DrawerHeader>
        <DrawerBody as={Flex} flexDirection="column" gridGap="4">
          {loading && (
            <Center flex={1} py="20">
              <Spinner size="xl" data-testid="action-history-loading" />
            </Center>
          )}

          {!loading && error && (
            <ErrorState
              title="An error occurred while loading Action History"
              subtitle="Please reach out to the technical support team for further assistance."
              h="50vh"
              // eslint-disable-next-line react/jsx-no-bind
              onClick={retry}
            />
          )}

          {!loading && !error && (
            <>
              <ActionHistoryFilters
                entries={entries}
                filter={filter}
                onChange={setFilter}
              />

              <Text
                fontSize="sm"
                color="gray.500"
                alignSelf="flex-end"
                data-testid="action-history-count"
              >
                {filteredEntries.length}{' '}
                {pluralize('record', filteredEntries.length)}
              </Text>

              {filteredEntries.length === 0 ? (
                <EmptyState
                  title="No records match the selected filters"
                  h="40vh"
                />
              ) : (
                <ActionHistoryTable entries={filteredEntries} />
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
